import React, { useState } from "react";
import { Link } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import {
  cilFolderOpen,
  cilAccountLogout,
  cilChartPie,
  cilX,
  cilCheckCircle,
  cilPlus,
  cilVideo,
  cilTruck,
  cilCamera,
  cilBell,
  cilUser,
  cilLayers,
  cilWarning,
  cilMap,
} from "@coreui/icons";
import LanguageSwitcher from "../../../components/LanguageSwitcher/LanguageSwitcher";

// Фирменный фиолетовый цвет CoreUI 5 (кнопка "Открыть")
const PRIMARY_COLOR = "#5856d6";

const DashboardHeader = ({
  t,
  organizationName,
  selectedDevices,
  processedDevices,
  isStaff,
  canDoMarkup,
  isSuperuser,
  setMultiModalVisible,
  setDisconnectModalVisible,
  handleSelectAll,
  handleAddClick,
  handleLogout,
  hasOmnicommConfig,
}) => {
  // Единый стиль кнопок: строго 38px в высоту, аккуратный шрифт
  const actionBtnClass =
    "btn btn-light border shadow-sm d-flex align-items-center gap-2 px-3 fw-bold flex-shrink-0 text-dark";
  const btnStyle = { height: "38px" };
  const iconStyle = { color: PRIMARY_COLOR };

  const allDisplayedSelected =
    processedDevices.length > 0 &&
    processedDevices.every((dev) => selectedDevices.includes(dev.id));
  
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  return (
    <div className="w-100 mb-4">
      {/* 1. ВЕРХНЯЯ СЛУЖЕБНАЯ ПАНЕЛЬ */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 gap-md-3 pb-3 border-bottom">
        <div className="d-none d-md-flex align-items-center gap-3 text-muted fw-bold">
          <span>{t("common.support", "Поддержка")}:</span>
          <a href="https://t.me/neimarker" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: PRIMARY_COLOR }}>
            TG@neimarker
          </a>
          <a href="https://max.ru/u/f9LHodD0cOJ924xRexRn2UcvZl8vFiSJdTkY8FHCiiVAx6_-pyD0LUBvUTo" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: PRIMARY_COLOR }}>
            MAX@neimarker
          </a>
          <a href="mailto:mail@neimarker.ru" className="text-decoration-none" style={{ color: PRIMARY_COLOR }}>
            MAIL@neimarker.ru
          </a>
        </div>

        <div className="d-md-none position-relative">
          <button
            type="button"
            onClick={() => setIsSupportOpen(!isSupportOpen)}
            className="btn btn-sm fw-bold rounded-pill px-3 py-1 d-flex align-items-center gap-1 shadow-sm"
            style={{ borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR, backgroundColor: "transparent" }}
          >
            <span>💬 {t("common.support", "Поддержка")}</span>
            <span style={{ fontSize: '10px', transform: isSupportOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>▼</span>
          </button>

          {isSupportOpen && (
            <>
              <div 
                className="position-fixed top-0 start-0 w-100 h-100" 
                style={{ zIndex: 1040 }} 
                onClick={() => setIsSupportOpen(false)} 
              />
              <div 
                className="position-absolute start-0 mt-2 p-2 bg-white rounded-3 shadow border" 
                style={{ zIndex: 1050, minWidth: '210px' }}
              >
                <div className="small text-muted fw-bold px-2 py-1 border-bottom mb-1">
                  {t("common.support", "Поддержка")}
                </div>
                <a 
                  href="https://t.me/neimarker" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="d-flex align-items-center gap-2 p-2 text-decoration-none text-dark rounded-2 hover-bg-light fw-semibold small"
                  onClick={() => setIsSupportOpen(false)}
                >
                  <span className="badge" style={{ backgroundColor: "rgba(88, 86, 214, 0.12)", color: PRIMARY_COLOR }}>TG</span>
                  <span>@neimarker</span>
                </a>
                <a 
                  href="https://max.ru/u/f9LHodD0cOJ924xRexRn2UcvZl8vFiSJdTkY8FHCiiVAx6_-pyD0LUBvUTo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="d-flex align-items-center gap-2 p-2 text-decoration-none text-dark rounded-2 hover-bg-light fw-semibold small"
                  onClick={() => setIsSupportOpen(false)}
                >
                  <span className="badge bg-info-subtle text-info-emphasis">MAX</span>
                  <span>@neimarker</span>
                </a>
                <a 
                  href="mailto:mail@neimarker.ru" 
                  className="d-flex align-items-center gap-2 p-2 text-decoration-none text-dark rounded-2 hover-bg-light fw-semibold small"
                  onClick={() => setIsSupportOpen(false)}
                >
                  <span className="badge bg-secondary-subtle text-secondary">MAIL</span>
                  <span>mail@neimarker.ru</span>
                </a>
              </div>
            </>
          )}
        </div>

        <div className="d-flex align-items-center gap-3 gap-md-4">
          <LanguageSwitcher />

          {isStaff && (
            <Link to="/documentation" className="text-decoration-none text-dark fw-bold d-flex align-items-center">
              <CIcon icon={cilFolderOpen} className="me-1 me-md-2" style={{ color: PRIMARY_COLOR }} /> 
              <span>{t("dashboard.documentation", "База знаний")}</span>
            </Link>
          )}

          <button onClick={handleLogout} className="btn btn-link text-danger fw-bold text-decoration-none d-flex align-items-center p-0">
            <CIcon icon={cilAccountLogout} className="me-1 me-md-2" /> 
            <span>Выйти</span>
          </button>
        </div>
      </div>

      {/* 2. ЗАГОЛОВОК */}
      <div className="d-flex flex-column align-items-center text-center mt-4 mb-2">
        <h2 className="fw-bold text-dark mb-1" style={{ fontSize: "2rem", letterSpacing: "-0.5px" }}>
          {organizationName ? organizationName : t("dashboard.title")}
        </h2>
        {organizationName && (
          <span className="text-muted fw-semibold">{t("dashboard.title")}</span>
        )}
      </div>

      {/* 3. ПАНЕЛЬ ИНСТРУМЕНТОВ */}
      <div className="bg-white p-3 rounded shadow-sm border mt-4">
        {/* Верхний ряд: Выбор, Добавление и Сводный отчет */}
        <div className="d-flex justify-content-center flex-wrap gap-2 mb-3 pb-3 border-bottom">
          <button 
            className="btn shadow-sm d-flex align-items-center gap-2 px-4 fw-bold text-white" 
            style={{ ...btnStyle, backgroundColor: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }} 
            onClick={handleAddClick}
          >
            <CIcon icon={cilPlus} /> Добавить технику
          </button>

          <button className={actionBtnClass} style={btnStyle} onClick={handleSelectAll}>
            <CIcon icon={allDisplayedSelected ? cilX : cilCheckCircle} style={iconStyle} />
            {allDisplayedSelected ? "Снять выбор" : "Выбрать все"}
          </button>

          {selectedDevices.length > 0 && (
            <>
              <button 
                className="btn btn-success text-white shadow-sm d-flex align-items-center gap-2 px-4 fw-bold" 
                style={btnStyle} 
                onClick={() => setMultiModalVisible(true)}
              >
                <CIcon icon={cilChartPie} />
                {t("dashboard.compare_selected", "Сводный отчет")} ({selectedDevices.length})
              </button>
              
              <button 
                className="btn btn-danger text-white shadow-sm d-flex align-items-center gap-2 px-4 fw-bold" 
                style={btnStyle} 
                onClick={() => setDisconnectModalVisible(true)}
              >
                <CIcon icon={cilWarning} />
                Журнал отключений
              </button>
            </>
          )}         
        </div>

        {/* Нижний ряд: Навигация (строго 38px) */}
        <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
          <Link to="/multi-monitor" className="text-decoration-none">
            <button className={actionBtnClass} style={btnStyle}>
              <CIcon icon={cilVideo} style={iconStyle} /> Мультимонитор
            </button>
          </Link>
          <Link to="/trucks" className="text-decoration-none">
            <button className={actionBtnClass} style={btnStyle}>
              <CIcon icon={cilTruck} style={iconStyle} /> Автопарк
            </button>
          </Link>
          <Link to="/truck-proofs" className="text-decoration-none">
            <button className={actionBtnClass} style={btnStyle}>
              <CIcon icon={cilCamera} style={iconStyle} /> Фотоконтроль
            </button>
          </Link>
          <Link to="/alerts" className="text-decoration-none">
            <button className={actionBtnClass} style={btnStyle}>
              <CIcon icon={cilBell} style={iconStyle} /> Уведомления
            </button>
          </Link>

          {isStaff && (
            <Link to="/users" className="text-decoration-none">
              <button className={actionBtnClass} style={btnStyle}>
                <CIcon icon={cilUser} style={iconStyle} /> Сотрудники
              </button>
            </Link>
          )}
          {(isSuperuser || canDoMarkup) && (
            <Link to="/markup" className="text-decoration-none">
              <button className={actionBtnClass} style={btnStyle}>
                <CIcon icon={cilLayers} style={iconStyle} /> AI Markup
              </button>
            </Link>
          )}
          {isSuperuser && (
            <Link to="/imu-viewer" className="text-decoration-none">
              <button className={actionBtnClass} style={btnStyle}>
                <CIcon icon={cilChartPie} style={iconStyle} /> IMU Viewer
              </button>
            </Link>
          )}
          {hasOmnicommConfig && (
            <Link to="/omnicomm-map" className="text-decoration-none">
              <button className={actionBtnClass} style={{ ...btnStyle, borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }}>
                <CIcon icon={cilMap} style={{ color: PRIMARY_COLOR }} /> Карта (Omnicomm)
              </button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;